import { Button, Modal } from "@mantine/core";
import type { AdditionalQuestionsConfig } from "../../../../types";
import DocsUpload from "../../../../pages/TaxOrganizer/Components/Custom/UploadDocArea/DocsUpload";
import {
  renderChoiceField,
  renderLongTextField,
  renderTextField,
} from "../../../../pages/TaxOrganizer/Components/Dynamic/FieldRenderers";
import "./PreviewQuestions.scss";
import { IconX } from "@tabler/icons-react";
import { generateTaxYear } from "../../../../utilities";

interface PreviewQuestionsProps {
  additionalQuestionsConfig: AdditionalQuestionsConfig;
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * PreviewQuestions component displays a modal that previews the additional questions
 * configured in the tax organizer. It renders each question based on its type, including
 * short input, long input, choice, and document upload fields. The modal can be closed
 * using the exit button.
 *
 * Props:
 * - additionalQuestionsConfig: An object containing the configuration for additional questions,
 *   including title, description, and an array of questions.
 * - opened: A boolean indicating whether the modal is open or closed.
 * - setOpened: A function to toggle the state of the modal.
 *
 * The component utilizes various field renderers for different question types and displays
 * them in a modal with an option to close the preview.
 */

const PreviewQuestions = ({
  additionalQuestionsConfig,
  opened,
  setOpened,
}: PreviewQuestionsProps) => {
  const previewQuestion = (
    additionalQuestionsConfig: AdditionalQuestionsConfig
  ) => {
    return additionalQuestionsConfig?.questions?.map((question: any) => {
      const key = question.id || `question-${question.title}`;

      switch (question.type.name) {
        case "shortInput":
          return (
            <div key={key}>
              {renderTextField(
                {
                  field: {
                    type: "textInput",
                    name: question?.id,
                    label: question.title,
                    description: question.description,
                    required: question?.isRequired,
                    placeholder: question?.placeholder,
                  },
                  value: "",
                  className: "additional-question-text-input",
                },
                {
                  groupIndex: 0,
                  handleFieldChange: () => {},
                  disabled: false,
                  errorMessage: "",
                }
              )}
            </div>
          );
        case "longInput":
          return (
            <div key={key}>
              {renderLongTextField(
                {
                  type: "textarea",
                  name: question?.id,
                  label: question.title,
                  description: question.description,
                  required: question?.isRequired,
                  placeholder: question?.placeholder,
                },
                0,
                "",
                () => {},
                false,
                "",
                "additional-question-long-text-input"
              )}
            </div>
          );
        case "choice":
          return (
            <div key={key}>
              {renderChoiceField(
                {
                  field: {
                    type: "choice",
                    name: question?.id,
                    label: question.title,
                    description: question.description,
                    options: question.type?.options,
                    multiselect: question.type?.isMultipleChoice,
                  },
                  value: "",
                },
                {
                  groupIndex: 0,
                  handleChange: () => {},
                  disabled: false,
                  errorMessage: "",
                  renderFields: () => {},
                }
              )}
            </div>
          );
        case "docUpload":
          return (
            <div key={key} className="docUpload">
              <span className="docUpload-title">
                <h2 className="docUpload-title-text">{question?.title}</h2>
                <p className="docUpload-title-required">
                  {question?.isRequired ? "*" : ""}
                </p>
              </span>
              <p className="docUpload-description">{question?.description}</p>
              <DocsUpload
                uploadTitleText={question.title}
                showUploadTitle={false}
                taxYear={generateTaxYear()}
              />
            </div>
          );
        default:
          return null;
      }
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      radius={0}
      size="xl"
      transitionProps={{ transition: "fade", duration: 200 }}
      className="preview-questions-modal"
      fullScreen
      withCloseButton={false}
    >
      <Button
        leftSection={<IconX />}
        className="close-icon"
        onClick={() => setOpened(false)}
        variant="transparent"
        color="#000"
      >
        Exit Preview
      </Button>
      <div className="body">
        <div className="sidebar"></div>
        <div className="additional-questions">
          <h2 className="title">{additionalQuestionsConfig.title}</h2>
          <p className="description">{additionalQuestionsConfig.description}</p>
          <div className="questions">
            {previewQuestion(additionalQuestionsConfig)}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PreviewQuestions;
